import { Container, useMediaQuery } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../common";
import {
  loadMoreProducts,
  ProductsList,
  selectProducts,
  selectProductStatus,
  useProducts,
} from ".";
import { NewCustomerDto, selectCustomer } from "../customers";
import { CustomerStarterKit } from "../customer-starter-kits";
import { Banner } from "../banners/types";

export type OnFilterFn = (category: string, value: string) => void;
export type OnSearchFn = (value: string) => void;
export type IsActiveFn = (category: string, value: string) => boolean;

export type Tag = {
  category: string;
  value: string;
  isDefault: boolean;
};

export const FETCH_LIMIT = 36;

const API_BASE_URL = process.env.VITE_ADMIN_BASE_URL;

export function ProductsPage() {
  const { refetchProducts } = useProducts();
  const [isOpen, setIsOpen] = useState(false);
  const [customerStarterKits, setCustomerStarterKits] = useState<
    CustomerStarterKit[]
  >([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [isLoadingKits, setIsLoadingKits] = useState(false);
  const [isLoadingBanners, setIsLoadingBanners] = useState(false);
  const { data: customerData } = useAppSelector(selectCustomer);
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProducts);
  const status = useAppSelector(selectProductStatus);

  const fetchStarterKits = async () => {
    const customerId = (customerData as NewCustomerDto)?.id;

    if (!customerId) {
      console.warn("No user ID available to fetch starter kits", {
        customerData,
      });
      setCustomerStarterKits([]);
      return;
    }

    try {
      setIsLoadingKits(true);

      const url = `${API_BASE_URL}/internal-api/customer-starter-kits/by-customer/${customerId}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch starter kits: ${response.statusText}`);
      }

      const data = await response.json();

      setCustomerStarterKits(data);
    } catch (error) {
      console.error("Error fetching starter kits:", error);
      setCustomerStarterKits([]);
    } finally {
      setIsLoadingKits(false);
    }
  };

  const fetchBanners = async () => {
    try {
      setIsLoadingBanners(true);

      const url = `${API_BASE_URL}/internal-api/banners`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch banners: ${response.statusText}`);
      }

      const data = await response.json();

      setBanners(data);
    } catch (error) {
      console.error("Error fetching banners:", error);
      setBanners([]);
    } finally {
      setIsLoadingBanners(false);
    }
  };

  useEffect(() => {
    if (customerData?.id) {
      fetchStarterKits();
    }
  }, [customerData]);

  useEffect(() => {
    fetchBanners();
  }, []);

  const onLoadMore = () => {
    dispatch(
      loadMoreProducts({
        page: products.currentPage + 1,
        limit: FETCH_LIMIT,
      })
    );
  };

  const [isLargerThan960] = useMediaQuery("(min-width: 1200px)");

  useEffect(() => {
    if (isLargerThan960 && !isOpen) {
      setIsOpen(true);
    }
    if (!isLargerThan960 && isOpen) {
      setIsOpen(false);
    }
  }, [isLargerThan960]);

  useEffect(() => {
    if (products.currentPage > 1 || products.data.length > 0) {
      return;
    }

    refetchProducts();
  }, []);

  const isLoading = status === "loading" || isLoadingKits || isLoadingBanners;
  const isIdle = status === "idle" && !isLoadingKits && !isLoadingBanners;

  return (
    <Container maxW="1248px" h="full" pb={8} px={{ base: 2, md: 4 }}>
      <ProductsList
        products={products.data}
        hasMore={products.hasMore}
        isIdle={isIdle}
        isLoading={isLoading}
        onLoadMore={onLoadMore}
        customerStarterKits={customerStarterKits}
        banners={banners}
      />
    </Container>
  );
}
